import React, { useMemo, useState } from 'react';
import {
  Box, Flex, Text, Icon, Button, Input, InputGroup, InputLeftElement, Select,
  Table, Thead, Tbody, Tr, Th, Td,
} from '@chakra-ui/react';
import { FiArrowLeft, FiFolder, FiSearch, FiPlus, FiEye, FiInbox } from 'react-icons/fi';

const shortDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' });
};

const FichasCarpetaView = ({ category, surveys = [], onBack, onOpenFicha, onNewFicha }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('recent');

  const pastel = category?.pastel || 'sky';

  const lastDate = useMemo(() => {
    if (surveys.length === 0) return null;
    return surveys.reduce((latest, item) =>
      new Date(item.created_at) > new Date(latest) ? item.created_at : latest, surveys[0].created_at);
  }, [surveys]);

  const filtered = useMemo(() => {
    let list = surveys;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      list = list.filter(item =>
        item.survey?.name?.toLowerCase().includes(term) || item.id?.toString().includes(term)
      );
    }
    list = [...list].sort((a, b) => {
      if (sortBy === 'name') return (a.survey?.name || '').localeCompare(b.survey?.name || '');
      if (sortBy === 'oldest') return new Date(a.created_at) - new Date(b.created_at);
      return new Date(b.created_at) - new Date(a.created_at);
    });
    return list;
  }, [surveys, searchTerm, sortBy]);

  return (
    <Box fontFamily="jakarta">
      <Button size="sm" variant="ghost" color="ink.700" fontWeight="700" borderRadius="9px" mb="16px"
        leftIcon={<Icon as={FiArrowLeft} boxSize="14px" />} onClick={onBack}>
        Volver a carpetas
      </Button>

      <Flex align="center" gap="20px" bg={`${pastel}.bg`} border="1px solid" borderColor="cream.line"
        borderRadius="20px" p="22px 26px" mb="22px" flexWrap="wrap">
        <Flex w="64px" h="64px" borderRadius="16px" bg="white" color={`${pastel}.ink`}
          align="center" justify="center" flexShrink={0}>
          <Icon as={category?.icon || FiFolder} boxSize="28px" />
        </Flex>
        <Box flex={1} minW="200px">
          <Text fontSize="20px" fontWeight="800" letterSpacing="-0.02em" color={`${pastel}.ink`}>
            {category?.displayName}
          </Text>
          <Text fontSize="13px" fontWeight="600" color={`${pastel}.ink`} opacity={0.85} mt="2px">
            Fichas de la categoría {category?.displayName}
          </Text>
          <Flex align="center" gap="14px" mt="12px" flexWrap="wrap">
            <Text fontSize="12.5px" fontWeight="700" color={`${pastel}.ink`}>
              {surveys.length} {surveys.length === 1 ? 'ficha' : 'fichas'}
            </Text>
            {lastDate && (
              <>
                <Box w="1px" h="14px" bg={`${pastel}.ink`} opacity={0.3} />
                <Text fontSize="12.5px" fontWeight="700" color={`${pastel}.ink`}>
                  Última: {shortDate(lastDate)}
                </Text>
              </>
            )}
          </Flex>
        </Box>
        <Button bg="white" color={`${pastel}.ink`} borderRadius="11px" fontWeight="700" fontSize="13.5px"
          flexShrink={0} _hover={{ opacity: 0.85 }} leftIcon={<Icon as={FiPlus} boxSize="14px" />}
          onClick={onNewFicha}>
          Nueva ficha aquí
        </Button>
      </Flex>

      <Flex gap="12px" mb="16px" flexWrap="wrap">
        <InputGroup maxW="320px" flex={1}>
          <InputLeftElement pointerEvents="none">
            <Icon as={FiSearch} color="ink.400" boxSize="14px" />
          </InputLeftElement>
          <Input placeholder={`Buscar en ${category?.displayName}…`} value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)} h="38px" borderRadius="10px" fontSize="13.5px"
            border="1.5px solid" borderColor="cream.line2" bg="white" color="ink.900"
            _focus={{ borderColor: 'coral.500', boxShadow: 'none' }} />
        </InputGroup>
        <Select value={sortBy} onChange={e => setSortBy(e.target.value)} w="190px" h="38px" borderRadius="10px"
          fontSize="13px" fontWeight="600" border="1.5px solid" borderColor="cream.line2" bg="white" color="ink.700"
          _focus={{ borderColor: 'coral.500', boxShadow: 'none' }}>
          <option value="recent">Más reciente primero</option>
          <option value="oldest">Más antigua primero</option>
          <option value="name">Por nombre</option>
        </Select>
      </Flex>

      {filtered.length === 0 ? (
        <Box bg="cream.bg2" borderRadius="16px" border="1.5px dashed" borderColor="cream.line2" p={10} textAlign="center">
          <Icon as={FiInbox} boxSize={9} color="ink.400" mb={3} />
          <Text fontSize="13.5px" color="ink.700" fontWeight="700">
            {surveys.length === 0 ? 'Sin fichas en esta categoría' : 'No se encontraron fichas'}
          </Text>
          <Text fontSize="12.5px" color="ink.400" mt={1} mb={4}>
            {surveys.length === 0 ? 'Crea la primera ficha de esta categoría' : 'Intenta modificar la búsqueda'}
          </Text>
          {surveys.length === 0 && (
            <Button size="sm" bg="coral.500" color="white" borderRadius="10px" fontWeight="700"
              _hover={{ bg: 'coral.600' }} leftIcon={<Icon as={FiPlus} boxSize="13px" />} onClick={onNewFicha}>
              Nueva ficha aquí
            </Button>
          )}
        </Box>
      ) : (
        <Box bg="white" border="1px solid" borderColor="cream.line" borderRadius="16px" overflow="hidden">
          <Table size="sm">
            <Thead bg="cream.bg2">
              <Tr>
                <Th fontSize="11px" color="ink.400" fontFamily="jakarta" letterSpacing="0.04em" border="none">Ficha</Th>
                <Th fontSize="11px" color="ink.400" fontFamily="jakarta" letterSpacing="0.04em" border="none">Año</Th>
                <Th fontSize="11px" color="ink.400" fontFamily="jakarta" letterSpacing="0.04em" border="none">Fecha</Th>
                <Th border="none" />
              </Tr>
            </Thead>
            <Tbody>
              {filtered.map(item => (
                <Tr key={item.id} cursor="pointer" _hover={{ bg: 'coral.tint' }} transition=".13s"
                  onClick={() => onOpenFicha(item)}>
                  <Td borderColor="cream.line">
                    <Flex align="center" gap="12px">
                      <Flex w="34px" h="34px" borderRadius="10px" bg={`${pastel}.bg`} color={`${pastel}.ink`}
                        align="center" justify="center" flexShrink={0}>
                        <Icon as={category?.icon || FiFolder} boxSize="15px" />
                      </Flex>
                      <Box minW={0}>
                        <Text fontSize="13.5px" fontWeight="800" color="ink.900" noOfLines={1}>
                          {item.survey?.name || 'Sin título'}
                        </Text>
                        <Text fontSize="11px" color="ink.400" fontWeight="600">#{item.id}</Text>
                      </Box>
                    </Flex>
                  </Td>
                  <Td borderColor="cream.line">
                    <Box as="span" px="9px" py="2px" borderRadius="999px" bg="cream.bg2" color="ink.700"
                      fontSize="12px" fontWeight="700">
                      {new Date(item.created_at).getFullYear()}
                    </Box>
                  </Td>
                  <Td borderColor="cream.line">
                    <Text fontSize="13px" color="ink.700" fontWeight="600">{shortDate(item.created_at)}</Text>
                  </Td>
                  <Td borderColor="cream.line" textAlign="right">
                    <Button size="xs" variant="ghost" color="ink.700" fontWeight="700" borderRadius="8px"
                      rightIcon={<Icon as={FiEye} boxSize="12px" />}
                      onClick={(e) => { e.stopPropagation(); onOpenFicha(item); }}>
                      Ver
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
};

export default FichasCarpetaView;
